import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';

import {
  createDefaultFilterForm,
  ERROR_TOAST_DURATION_MS,
  FilterForm,
  formatApiDate,
  ITEMS_PER_PAGE,
  LIST_QUERY_KEY,
  STORE_LIST_QUERY_KEY,
  SUCCESS_TOAST_DURATION_MS,
  ToastLocationState,
} from '@/pages/externals/mos-pos-resend/service';
import { routes } from '@/router';
import { getMosPosOrderList, getMosPosStoreList } from '@/services/MosPosOrder';
import { MosPosOrderListItem } from '@/services/MosPosOrder/type';

/**
 * @param enabled 一覧と店舗検索の候補を取得してよいかどうかです。呼び出し側のアクセス判定を渡します。
 *   アカウントの読み込みが完了するまでは false になるため、判定前のリクエストが
 *   発生しません。
 */
export const useMosPosResendListPage = (enabled = true) => {
  const history = useHistory();
  const location = useLocation<ToastLocationState>();
  const toast = useToast();

  const defaultFilterForm = useMemo(() => createDefaultFilterForm(), []);
  /** 入力中の検索条件 */
  const [form, setForm] = useState<FilterForm>(defaultFilterForm);
  /** 検索ボタンで確定した検索条件。react-query の queryKey に使う */
  const [applied, setApplied] = useState<FilterForm>(defaultFilterForm);
  const [page, setPage] = useState(1);

  const queryParams = useMemo(
    () => ({
      deliveryServiceId:
        applied.deliveryServiceId === ''
          ? undefined
          : Number(applied.deliveryServiceId),
      authStoreIds:
        applied.authStoreId === null ? undefined : [applied.authStoreId],
      orderDateStart: formatApiDate(applied.orderDateStart),
      orderDateEnd: formatApiDate(applied.orderDateEnd),
    }),
    [applied]
  );

  const { data, isLoading, error, isFetching } = useQuery(
    [LIST_QUERY_KEY, queryParams, page],
    () =>
      getMosPosOrderList({
        ...queryParams,
        page,
        pageSize: ITEMS_PER_PAGE,
      }),
    { enabled, keepPreviousData: true }
  );

  // 店舗検索の候補。一覧と同じアクセス判定でゲートする
  const {
    data: storeListData,
    isLoading: isLoadingStores,
    error: storeListError,
  } = useQuery([STORE_LIST_QUERY_KEY], () => getMosPosStoreList(), {
    enabled,
  });

  // 候補の再取得で選択中の拠点が候補から消えた場合や、候補が 1 件以下の固定表示に
  // なった場合は、画面に出ない店舗条件が検索で送られないよう選択を解除する。
  // 読み込み中と取得失敗時は data が変わらないため、選択は維持される
  useEffect(() => {
    const stores = storeListData?.stores;
    if (stores === undefined) return;
    setForm((prev) => {
      if (prev.authStoreId === null) return prev;
      const isSelectable =
        stores.length > 1 &&
        stores.some((store) => store.auth_store_id === prev.authStoreId);
      return isSelectable ? prev : { ...prev, authStoreId: null };
    });
  }, [storeListData]);

  // 検索は検索ボタン押下時のみ実行する (入力の都度は実行しない)
  const handleSearch = useCallback(() => {
    setApplied(form);
    setPage(1);
  }, [form]);

  const handleChangeDeliveryServiceId = useCallback((value: string) => {
    setForm((prev) => ({ ...prev, deliveryServiceId: value }));
  }, []);

  const handleChangeAuthStoreId = useCallback((value: number | null) => {
    setForm((prev) => ({ ...prev, authStoreId: value }));
  }, []);

  const handleChangeOrderDateStart = useCallback((date: Date) => {
    setForm((prev) => ({ ...prev, orderDateStart: date }));
  }, []);

  const handleChangeOrderDateEnd = useCallback((date: Date) => {
    setForm((prev) => ({ ...prev, orderDateEnd: date }));
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  /** 行クリック時は再送信可否に応じて対応する確認画面へ遷移する */
  const handleRowClick = useCallback(
    (row: MosPosOrderListItem) => {
      const path = row.can_resend
        ? routes.MosPosResendConfirm.path.replace(':orderId', row.order_id)
        : routes.MosPosCancelConfirm.path.replace(':orderId', row.order_id);
      history.push(path);
    },
    [history]
  );

  const handleResendClick = useCallback(
    (row: MosPosOrderListItem) => {
      history.push(
        routes.MosPosResendConfirm.path.replace(':orderId', row.order_id)
      );
    },
    [history]
  );

  const handleCancelClick = useCallback(
    (row: MosPosOrderListItem) => {
      history.push(
        routes.MosPosCancelConfirm.path.replace(':orderId', row.order_id)
      );
    },
    [history]
  );

  // 確認画面から戻ってきたときの成功トースト
  useEffect(() => {
    const succeeded = location.state?.toast;
    if (succeeded === undefined) return;

    toast({
      title: `${succeeded.webOrderId} を${
        succeeded.type === 'resend' ? '再送信' : '取消'
      }しました`,
      status: 'success',
      duration: SUCCESS_TOAST_DURATION_MS,
      isClosable: true,
    });
    // リロード時に再表示されないよう state を消す
    history.replace(routes.MosPosResendList.path, {});
    // NOTE 初回マウント時のみ判定する
  }, []);

  // API エラーのトースト
  useEffect(() => {
    if (error === null || error === undefined) return;
    toast({
      title: 'エラーが発生しました。時間をおいて再度お試しください。',
      status: 'error',
      duration: ERROR_TOAST_DURATION_MS,
      isClosable: true,
    });
    // NOTE エラーが変化したときだけトーストを出す
  }, [error]);

  // 店舗検索の候補取得エラーも一覧と同じ共通エラートーストで通知する
  useEffect(() => {
    if (storeListError === null || storeListError === undefined) return;
    toast({
      title: 'エラーが発生しました。時間をおいて再度お試しください。',
      status: 'error',
      duration: ERROR_TOAST_DURATION_MS,
      isClosable: true,
    });
    // NOTE エラーが変化したときだけトーストを出す
  }, [storeListError]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const stores = storeListData?.stores ?? [];

  return {
    form,
    page,
    items,
    total,
    stores,
    // enabled が false の間 react-query は idle になり isLoading が false になります。
    // そのまま返すと「候補 0 件」の固定表示を一瞬描画してしまうため、読み込み中として扱います。
    isLoadingStores: !enabled || isLoadingStores,
    hasStoreError: storeListError !== null && storeListError !== undefined,
    // enabled が false の間 react-query は idle になり isLoading が false になります。
    // そのまま返すと空状態を描画してしまうため、読み込み中として扱います。
    isLoading: !enabled || isLoading,
    isFetching,
    hasError: error !== null && error !== undefined,
    handlers: {
      handleSearch,
      handleChangeDeliveryServiceId,
      handleChangeAuthStoreId,
      handleChangeOrderDateStart,
      handleChangeOrderDateEnd,
      handlePageChange,
      handleRowClick,
      handleResendClick,
      handleCancelClick,
    },
  };
};
